import React, { useMemo } from 'react'
import { Accordion, AccordionSummary, AccordionDetails } from '@mui/material'
import { INTERAX_COLORS } from '../../../constants/interaxColors'
import { useGetSprint, useTeam } from '../../../apiLayer/queryHooks/queries'
import { useUser } from '../../../hooks/useUser'
import { SprintProgressHeader } from './sprint/SprintProgressHeader'
import { SprintActivitiesList } from './sprint/SprintActivitiesList'
import { getInitials } from './sprint/sprintHelpers'

export interface SprintGamerProgressProps {
  sprintId: string
  userTeamIds: string[]
  onActivityClick?: () => void
  className?: string
}

export const SprintGamerProgress: React.FC<SprintGamerProgressProps> = ({
  sprintId,
  userTeamIds,
  onActivityClick,
  className = ''
}) => {
  // Fetch sprint data
  const { data: sprintResponse, isLoading: isSprintLoading } = useGetSprint(sprintId)
  const sprint = sprintResponse?.sprint

  // Find user's team in the sprint
  const userTeamId = useMemo(() => {
    if (!sprint || !userTeamIds.length) return undefined
    return sprint.teams.find((team: any) => team.teamId && userTeamIds.includes(team.teamId))?.teamId
  }, [sprint, userTeamIds])

  // Fetch team data
  const { data: teamResponse } = useTeam(userTeamId || '')
  const team = teamResponse?.data

  // The team's members, as the sprint lists them
  const teamMembers = useMemo(
    () => (sprint?.participants || []).filter((participant) => !!userTeamId && participant.teamId === userTeamId),
    [sprint, userTeamId]
  )

  // Fetch facilitator data
  const facilitatorId = sprint?.facilitatedByUserIds?.[0]
  const { data: facilitatorData } = useUser(facilitatorId)

  // Calculate completion percentage
  const completionPercentage = useMemo(() => {
    if (!sprint || !userTeamId) {
      return 0
    }

    // An activity counts as done for the team if it explicitly completed for them, or it has ended
    // (timed activities end for everyone — legacy docs may not list each team in completedActivityTeamIds).
    const completed = sprint.activities.filter(
      (activity: any) => !!activity.endedAt || (activity.completedActivityTeamIds || []).includes(userTeamId)
    ).length

    const total = sprint.activities.length || 1
    return Math.round((completed / total) * 100)
  }, [sprint, userTeamId])

  // Get member initials
  const { memberInitials, remainingMemberCount } = useMemo(() => {
    const displayMembers = teamMembers.slice(0, 3)
    const remainingCount = Math.max(0, teamMembers.length - 3)

    const initials = displayMembers.map((member) => getInitials(member.name || 'U'))

    return {
      memberInitials: initials,
      remainingMemberCount: remainingCount
    }
  }, [teamMembers])

  if (isSprintLoading || !sprint) {
    return null
  }

  if (!sprint.activities || sprint.activities.length === 0) {
    return null
  }

  return (
    <Accordion
      className={className}
      expanded={true}
      onChange={() => {}} // Prevent state changes - accordion is always expanded
      sx={{
        backgroundColor: INTERAX_COLORS.heritageBlue40,
        backgroundImage: 'none',
        borderRadius: '12px !important',
        border: `1px solid ${INTERAX_COLORS.white10}`,
        boxShadow: 'none',
        '&:before': {
          display: 'none'
        },
        '&.Mui-expanded': {
          margin: 0
        }
      }}
    >
      <AccordionSummary
        onClick={(e) => e.preventDefault()}
        sx={{
          minHeight: 'auto',
          padding: 2,
          '& .MuiAccordionSummary-content': {
            margin: '0px 0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 2,
            flexWrap: 'wrap'
          },
          '&.Mui-expanded': {
            minHeight: 'auto',
            '& .MuiAccordionSummary-content': {
              margin: '0px 0'
            }
          }
        }}
      >
        <SprintProgressHeader
          sprintTitle={sprint.title}
          facilitatorData={facilitatorData}
          team={team}
          memberInitials={memberInitials}
          remainingMemberCount={remainingMemberCount}
          completionPercentage={completionPercentage}
        />
      </AccordionSummary>

      <AccordionDetails
        sx={{
          padding: 2,
          paddingTop: 0
        }}
      >
        <SprintActivitiesList
          activities={sprint.activities}
          userTeamId={userTeamId}
          onActivityClick={onActivityClick}
        />
      </AccordionDetails>
    </Accordion>
  )
}
